import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import SimpleEmbedForm from "@/components/posit/SimpleEmbedForm";
import PositDeleted from "@/components/posit/PositDeleted";

const MinimalEmbed = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [interactions, setInteractions] = useState(0);

  // Fetch the posit data - hook must be called before any conditional returns
  const { data: posit, isLoading, error } = useQuery({
    queryKey: ["minimal-embed-posit", id],
    enabled: !!id, // Only run query if id is present
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posits")
        .select("*")
        .eq("id", id)
        .single();

      if (error || !data || data.embed_enabled !== true) {
        throw new Error("Posit not available");
      }

      return data;
    },
  });

  // Early return after hooks are called
  if (!id) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', fontFamily: 'system-ui' }}>
        Invalid embed URL
      </div>
    );
  }

  // Always use dark theme
  const hideWatermark = searchParams.get("watermark") === "false";
  const placeholder = searchParams.get("placeholder") || "Ask anything...";

  // Extract embed configuration from URL params
  const embedConfig = {
    theme: "dark" as const,
    showBranding: !hideWatermark,
    maxWidth: searchParams.get("width") || "100%",
    borderRadius: searchParams.get("radius") || "8px",
    primaryColor: searchParams.get("color") || "#2563eb",
    domain: typeof window !== "undefined" ? window.location.hostname : "localhost",
    placeholder: placeholder
  };

  // Track interactions for analytics
  const handleInteraction = (type: 'submit' | 'click') => {
    setInteractions(prev => prev + 1);
    
    // Track the interaction (you can enhance this with proper analytics)
  };

  if (isLoading) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        fontFamily: 'system-ui',
        backgroundColor: '#1a1a1a',
        color: '#ffffff'
      }}>
        Loading...
      </div>
    );
  }

  if (error || !posit) {
    return (
      <PositDeleted
        context="embed"
        isAuthenticated={false}
        positId={id}
      />
    );
  }

  return (
    <div style={{ 
      width: '100%',
      margin: '0 auto',
      backgroundColor: '#1a1a1a'
    }}>
      <SimpleEmbedForm 
        posit={posit}
        embedConfig={embedConfig}
        onInteraction={handleInteraction}
      />
    </div>
  );
};

export default MinimalEmbed; 